/**
 * `/` — Dashboard-Startseite (P1, docs/KONZEPT-ADMIN-BEREICH.md § "Informationsarchitektur & UI" +
 * § "Dashboard": "KPI-Zeile (Mandanten nach Status, KI-Kosten Plattform/Monat, Mails+Bounce-Quote,
 * Systemfehler 24h, aktive Impersonationen), Warnungen-Liste (Fehler, Bounces, `pending`
 * Absenderdomain >7 Tage, offene Onboarding-Gaps >14 Tage, inaktiv >30 Tage), letzte 10
 * Admin-Aktionen", docs/LOOP-ADMIN-BEREICH.md P1, sechstes Kriterium).
 *
 * PRAEZISIERUNGEN gegenueber dem Konzept-Wortlaut — nur mit tatsaechlich vorhandenen Routen
 * gebaut, nichts erfunden (Auftrag "Wichtig: nur mit tatsaechlich vorhandenen Datenquellen bauen"):
 *
 *  - **"Mandanten nach Status" ENTFAELLT als eigene KPI-Kachel.** `tenants` hat noch keine
 *    Status-Spalte (kommt erst mit Migration 375, P2, docs/KONZEPT-ADMIN-BEREICH.md §
 *    "Architektur-Entscheidung" Punkt 5) — eine Status-Aufschluesselung wuerde Daten erfinden, die
 *    heute nicht existieren. Die Mandanten-ANZAHL (Laenge des `GET /api/admin/nutzung`-Arrays,
 *    EINE Zeile je Mandant) steht stattdessen als Kontext im `PageHeader`-Untertitel, nicht als
 *    eigene Kachel — die KPI-Zeile hat deshalb VIER statt fuenf Kacheln.
 *  - **"Systemfehler 24h" -> "Systemfehler (7 Tage)".** `GET /api/admin/nutzung`
 *    (`platform_admin_nutzung_uebersicht`, Migration 373, siehe Kopfkommentar
 *    `packages/db/sql/373_platform_admin_nutzung.sql`) liefert `fehler7d` je Mandant — ein reines
 *    24h-Fenster existiert in der Aggregation nicht ("die Fenster [7/30 Tage] stecken bewusst in
 *    den Feldnamen selbst"). Diese Kachel summiert `fehler7d` ueber ALLE Mandanten und beschriftet
 *    den tatsaechlichen Zeitraum ehrlich, statt "24h" vorzutaeuschen — identische Praezisierung wie
 *    in `tenant-detail/GesundheitTab.tsx` ("Fehler (7 Tage)").
 *  - **"KI-Kosten Plattform/Monat" -> "KI-Kosten (30 Tage)".** Dieselbe Route liefert
 *    `kiKosten30dCents` als ROLLIERENDES 30-Tage-Fenster, keinen Kalendermonat — identische
 *    Praezisierung wie in `tenant-detail/NutzungTab.tsx`.
 *  - **Bounce-Quote traegt das "Stand letzter Quelle"-Label sichtbar** (`zusatz.bounceQuoteHinweis`
 *    aus der Route, `BOUNCE_QUOTE_HINWEIS` in `apps/api/src/routes/admin.ts`) — Resend ist seit der
 *    Abloesung nicht mehr aktiv, ein Nachfolge-Zustellweg (Paket C) ist noch nicht live
 *    (CLAUDE.md § "Angebundene Dienste").
 *  - **Warnungen-Liste: NUR "Mandanten mit Fehlern" ist aus bestehenden Routen ableitbar**
 *    (`fehler7d > 0` aus `GET /api/admin/nutzung`). Bounces (`GET /api/admin/nutzung` liefert nur
 *    die aggregierte `bounce30d`-Zahl je Mandant, keine Schwelle "Bounces 7 Tage" und keine
 *    Einzel-Mail-Liste — dieselbe Praezisierung wie in `GesundheitTab.tsx`), `pending`
 *    Absenderdomain >7 Tage (keine Admin-Route fuer `tenant_settings`/Absenderdomain-Status vor
 *    Migration 378, P4), offene Onboarding-Gaps >14 Tage (keine Admin-Route fuer
 *    `computeOnboardingGaps`) und Inaktivitaet >30 Tage (per se aus `letzteAktivitaet` ableitbar,
 *    aber das Konzept nennt explizit "inaktiv >30 Tage" als SEPARATE Warnungsart mit eigener
 *    Schwelle-Bedeutung — bewusst nicht mit "Mandanten mit Fehlern" vermischt, um keine zweite,
 *    nicht verlangte Kategorie zu erfinden) fehlen NUR wegen fehlender Routen, bewusst nicht
 *    erfunden — offener Punkt fuer eine spaetere Iteration (siehe Bericht).
 *
 * NAVIGATION: die "Aktive Impersonationen"-Kachel und "Alle ansehen" bei den letzten
 * Admin-Aktionen bekommen `onNavigate` von `pages/admin/index.tsx` durchgereicht (identischer
 * Mechanismus wie `AdminShell`s Nav-Buttons, `useAdminNavigation().navigiereZu`) statt `KpiCard`s
 * `to`-Prop (react-router `Link`): der Admin-Bereich navigiert bewusst OHNE `<Route>`-Registrierung
 * (`hooks/useAdminNavigation.ts` Kopfkommentar) — ein `Link`-Klick wuerde die URL per
 * `history.pushState` aendern, aber KEIN `popstate`-Ereignis ausloesen, auf das dieser Mini-Router
 * lauscht; die sichtbare Seite bliebe hinter der URL zurueck. `KpiCard` bekommt dafuer den neuen,
 * abwaertskompatiblen `onClick`-Alternativpfad zu `to` (siehe `ui/components.tsx`).
 *
 * DREI UNABHAENGIGE LADEZUSTAENDE: `GET /api/admin/nutzung` traegt die KPI-Zeile UND die
 * Warnungen-Liste (Haupt-Datenquelle — laedt sie nicht, bleibt die ganze Seite auf Lade-/
 * Fehleranzeige), waehrend `GET /api/admin/impersonations?nurAktive=true` (eine Kachel) und
 * `GET /api/admin/audit-log?limit=10` (der Aktionen-Abschnitt) EIGENSTAENDIG laden/fehlschlagen
 * koennen, ohne die restliche Seite zu blockieren — dieselbe Unabhaengigkeits-Idee wie mehrere
 * Tabs, nur ohne Tab-Wechsel (alles ist immer sichtbar).
 */
import { useApi } from "../../hooks/useApi";
import { EmptyState, ErrorNote, KpiCard, LoadingRows, PageHeader } from "../../ui/components";
import { formatDateTime, money } from "../../ui/format";

/** 1:1 aus `apps/api/src/routes/admin.ts` `NutzungUebersichtRow` uebernommen (JSON-Antwort:
 *  `letzteAktivitaet` kommt als ISO-String oder `null` an, nicht als `Date`). */
interface NutzungUebersichtRow {
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  memberCount: number;
  letzteAktivitaet: string | null;
  fehler7d: number;
  mails30d: number;
  bounce30d: number;
  bounceQuote30d: number | null;
  kiKosten30dCents: number;
}

/** 1:1 aus `apps/api/src/routes/admin.ts` `AuditLogRow` uebernommen (wie in `AuditLogSeite.tsx`). */
interface AuditLogRow {
  id: string;
  adminId: string;
  action: string;
  tenantId: string | null;
  tenantSlug: string | null;
  tenantName: string | null;
  detail: unknown;
  createdAt: string;
}

/** Nur die fuer den Zaehler benoetigten Felder aus `ImpersonationListItem` — die Kachel zeigt
 *  ausschliesslich die ANZAHL, keine Details (die stehen auf `/impersonationen`). */
interface ImpersonationListItem {
  id: string;
}

/** Kurzfassung fuer die Tabellenzeile — 1:1 aus `AuditLogSeite.tsx` `kurzfassung()` uebernommen
 *  (etwas knapper gekappt: die Dashboard-Spalte ist schmaler als die volle `/audit`-Tabelle). */
function kurzfassung(detail: unknown): string {
  if (detail == null) return "–";
  if (typeof detail === "string") return detail || "–";
  try {
    const json = JSON.stringify(detail);
    if (!json || json === "{}") return "–";
    return json.length > 70 ? `${json.slice(0, 70)}…` : json;
  } catch {
    return "–";
  }
}

/** `bounced / mails` als deutsch formatierte Prozentzahl, `null` bei fehlendem Nenner — 1:1 aus
 *  `tenant-detail/NutzungTab.tsx` `formatQuote()` uebernommen. */
function formatQuote(value: number | null): string {
  if (value == null) return "–";
  return `${(value * 100).toFixed(1).replace(".", ",")} %`;
}

export function DashboardSeite({ onNavigate }: { onNavigate: (pfad: string) => void }) {
  const nutzung = useApi<NutzungUebersichtRow[], { bounceQuoteHinweis?: string }>("/api/admin/nutzung");
  const impersonations = useApi<ImpersonationListItem[]>("/api/admin/impersonations?nurAktive=true");
  const audit = useApi<AuditLogRow[]>("/api/admin/audit-log?limit=10");

  if (nutzung.loading) {
    return (
      <>
        <PageHeader title="Dashboard" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }, (_, i) => (
            <div
              key={i}
              className="rounded-[14px] border border-surface-variant bg-surface-container-lowest p-4 dark:border-white/10 dark:bg-night-card"
            >
              <LoadingRows rows={1} />
            </div>
          ))}
        </div>
      </>
    );
  }

  if (nutzung.error || !nutzung.data) {
    return (
      <>
        <PageHeader title="Dashboard" />
        <ErrorNote message={nutzung.error ?? "Unbekannter Fehler."} onRetry={nutzung.reload} />
      </>
    );
  }

  const mandanten = nutzung.data;
  const totalKiCents = mandanten.reduce((summe, r) => summe + r.kiKosten30dCents, 0);
  const totalMails = mandanten.reduce((summe, r) => summe + r.mails30d, 0);
  const totalBounces = mandanten.reduce((summe, r) => summe + r.bounce30d, 0);
  const totalFehler = mandanten.reduce((summe, r) => summe + r.fehler7d, 0);
  const gesamtQuote = totalMails > 0 ? totalBounces / totalMails : null;
  const mandantenMitFehlern = mandanten.filter((r) => r.fehler7d > 0);

  const impersonationsGeladen = !impersonations.loading && !impersonations.error;
  const aktiveAnzahl = impersonations.data?.length ?? 0;
  const impersonationsWert: string | number = impersonations.loading ? "…" : impersonations.error ? "–" : aktiveAnzahl;

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle={`${mandanten.length} Mandant${mandanten.length === 1 ? "" : "en"} auf der Plattform`}
      />

      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard label="KI-Kosten (30 Tage)" value={money(totalKiCents)} icon="smart_toy" />
          <KpiCard
            label="Bounce-Quote (30 Tage)"
            value={formatQuote(gesamtQuote)}
            icon="report"
            iconTone={gesamtQuote != null && gesamtQuote > 0 ? "warn" : "success"}
          />
          <KpiCard
            label="Systemfehler (7 Tage)"
            value={totalFehler}
            icon="error"
            iconTone={totalFehler > 0 ? "warn" : "success"}
          />
          <KpiCard
            label="Aktive Impersonationen"
            value={impersonationsWert}
            icon="visibility"
            iconTone={impersonationsGeladen && aktiveAnzahl > 0 ? "danger" : "success"}
            onClick={() => onNavigate("/impersonationen")}
          />
        </div>
        {nutzung.zusatz?.bounceQuoteHinweis && (
          <p className="font-body-sm text-[12px] text-secondary-fixed-dim">{nutzung.zusatz.bounceQuoteHinweis}</p>
        )}

        <section>
          <h2 className="mb-3 font-headline-sm text-headline-sm font-bold text-on-background dark:text-inverse-on-surface">
            Warnungen
          </h2>
          {mandantenMitFehlern.length === 0 ? (
            <div className="card">
              <EmptyState
                icon="check_circle"
                title="Keine Auffälligkeiten"
                hint="Kein Mandant hat in den letzten 7 Tagen Systemfehler."
              />
            </div>
          ) : (
            <div className="card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="handy-karten w-full min-w-[420px] border-collapse text-left">
                  <thead>
                    <tr>
                      <th className="table-header">Mandant</th>
                      <th className="table-header">Fehler (7 Tage)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-outline-variant/30 dark:divide-outline/30">
                    {mandantenMitFehlern.map((r) => (
                      <tr key={r.tenantId}>
                        <td className="table-cell font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">
                          {r.tenantName}{" "}
                          <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                            ({r.tenantSlug})
                          </span>
                        </td>
                        <td className="table-cell font-body-sm text-body-sm text-error">{r.fehler7d}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <p className="mt-2 font-body-sm text-[12px] text-secondary-fixed-dim">
            Weitere Warnungsarten (Bounces, ausstehende Absenderdomain-Verifizierung, offene
            Onboarding-Punkte, Inaktivität) sind über die Admin-API aktuell nicht verfügbar — offener
            Punkt für eine spätere Iteration.
          </p>
        </section>

        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-headline-sm text-headline-sm font-bold text-on-background dark:text-inverse-on-surface">
              Letzte Admin-Aktionen
            </h2>
            <button type="button" className="btn-secondary !py-1.5 !text-[12px]" onClick={() => onNavigate("/audit")}>
              Alle ansehen
            </button>
          </div>
          {audit.loading ? (
            <div className="card">
              <LoadingRows rows={5} />
            </div>
          ) : audit.error ? (
            <ErrorNote message={audit.error} onRetry={audit.reload} />
          ) : !audit.data || audit.data.length === 0 ? (
            <div className="card">
              <EmptyState icon="fact_check" title="Noch keine Admin-Aktionen" />
            </div>
          ) : (
            <div className="card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="handy-karten w-full min-w-[620px] border-collapse text-left">
                  <thead>
                    <tr>
                      <th className="table-header">Zeit</th>
                      <th className="table-header">Admin</th>
                      <th className="table-header">Aktion</th>
                      <th className="table-header">Mandant</th>
                      <th className="table-header">Kurzfassung</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-outline-variant/30 dark:divide-outline/30">
                    {audit.data.map((zeile) => (
                      <tr key={zeile.id}>
                        <td className="table-cell whitespace-nowrap font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                          {formatDateTime(zeile.createdAt)}
                        </td>
                        <td className="table-cell font-mono text-body-sm text-on-background dark:text-inverse-on-surface">
                          {zeile.adminId}
                        </td>
                        <td className="table-cell font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">
                          {zeile.action}
                        </td>
                        <td className="table-cell font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                          {zeile.tenantName ?? "–"}
                        </td>
                        <td className="table-cell max-w-[280px] truncate font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                          {kurzfassung(zeile.detail)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      </div>
    </>
  );
}
